import { useEffect, useState } from 'react'
import { Alert, Paper, Stack, Switch, Typography } from '@mui/material'
import { configurarColetaDiaria, situacaoColetaDiaria, type ColetaDiaria } from '../api/admin'

const quando = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: 'America/Sao_Paulo' })

/**
 * Liga e desliga a coleta automática: todo dia às 6h o banco chama o coletor de cada conta
 * real e ativa. Sem conta real, ligar não tem efeito — o card diz isso.
 */
/** `versao` muda quando contas mudam na tela: o card recarrega a contagem. */
export function ColetaDiariaCard({ versao = 0 }: { versao?: number }) {
  const [s, setS] = useState<ColetaDiaria | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    situacaoColetaDiaria()
      .then(setS)
      .catch((e: Error) => setErro(e.message))
  }, [versao])

  const alternar = async (ativa: boolean) => {
    setSalvando(true)
    setErro(null)
    try {
      setS(await configurarColetaDiaria(ativa))
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setSalvando(false)
    }
  }

  if (!s && !erro) return null

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} useFlexGap flexWrap="wrap">
        <Stack sx={{ mr: 'auto', maxWidth: 720 }} spacing={0.25}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
            Coleta diária automática
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {s?.ativa
              ? `Ligada: todo dia às ${s.horario} o sistema atualiza os últimos 7 dias de ${s.contas} ${s.contas === 1 ? 'conta' : 'contas'}.`
              : 'Desligada: os dados só entram pelo botão “Coletar agora”.'}{' '}
            {s?.ultima ? `Última coleta automática: ${quando.format(new Date(s.ultima))}.` : ''}
          </Typography>
          {s && s.contas === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Nenhuma conta real com coletor ainda — cadastre a conta de uma marca e faça a primeira coleta.
            </Typography>
          ) : null}
        </Stack>
        <Switch
          checked={Boolean(s?.ativa)}
          disabled={!s || !s.disponivel || salvando}
          onChange={(e) => alternar(e.target.checked)}
          slotProps={{ input: { 'aria-label': 'Coleta diária automática' } }}
        />
      </Stack>
      {s && !s.disponivel ? (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          O agendador do banco (pg_cron e pg_net) não está ligado neste projeto.
        </Alert>
      ) : null}
      {erro ? (
        <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      ) : null}
    </Paper>
  )
}
